import { useState } from 'react';
import { OUTSIDER_NAME_MAX, normalizeOutsiderName, subOptions } from '@topspin/shared';
import type { Override, Session } from '@topspin/shared';
import { ApiError } from '../api.js';
import { useCreateOut, useUpdateOut, type GroupView } from '../data.js';
import { useOnline } from '../hooks.js';

type Choice = { kind: 'reserve'; playerId: string } | { kind: 'outsider' };

/**
 * "I can't make it" (§6.1). Pick a sub — the reserves first, by name, which is itself the
 * nudge to try them before going outside the group, and among them whoever can play ahead of
 * whoever can't, in the order the card lists them (§5a). A choice is required to save.
 *
 * Two shapes, and the difference is whose slot the override is about. Normally it's the signed-in
 * player's own, and `outPlayerId` defaults to them. When a reserve who agreed to sub can't make
 * it either, the slot still belongs to the player they were covering: the same override gets
 * re-pointed at whoever takes over, so the slot never goes uncovered and the original player
 * doesn't have to be involved. The copy switches to match, and `onSaved` is where the caller
 * records that the player handing off isn't coming.
 */
export default function OutFlow({
  group,
  seasonId,
  session,
  playerId,
  outPlayerId = playerId,
  existing,
  onSaved,
  onClose,
}: {
  group: GroupView;
  seasonId: string;
  session: Session;
  /** The signed-in player — the one this is being saved by. */
  playerId: string;
  /** Whose scheduled slot the override covers. Differs from `playerId` on a hand-off. */
  outPlayerId?: string;
  existing?: Override;
  /** Run once the override is saved, before closing. Used to record the hand-off. */
  onSaved?: () => void;
  onClose: () => void;
}) {
  const handingOff = outPlayerId !== playerId;
  const editing = Boolean(existing);
  // Pre-selected only when editing your own override, where the current sub is the answer to
  // change. On a hand-off the current sub is the player doing the handing off, and offering
  // them back — pre-ticked, and absent from the list, so nothing looks selected — would leave
  // Save enabled on a no-op.
  const seed = handingOff ? undefined : existing;
  const [choice, setChoice] = useState<Choice | undefined>(
    seed?.subKind === 'reserve' && seed.subPlayerId
      ? { kind: 'reserve', playerId: seed.subPlayerId }
      : seed?.subKind === 'outsider'
        ? { kind: 'outsider' }
        : undefined,
  );
  const [outsiderName, setOutsiderName] = useState(seed?.subName ?? '');
  const online = useOnline();
  const create = useCreateOut();
  const update = useUpdateOut();
  const pending = create.isPending || update.isPending;

  // Scoped to the slot being filled, not to the player filling it — so a hand-off can't offer
  // the reserve doing the handing off (they're the committed sub) or the player they cover.
  // Comes back available-first, the same order the card's sub list is read down in.
  const reserves = subOptions(
    group.config,
    session,
    group.overrides,
    outPlayerId,
    group.availabilities,
  );
  const trimmedName = normalizeOutsiderName(outsiderName);
  const canSave =
    online &&
    !pending &&
    (choice?.kind === 'reserve' || (choice?.kind === 'outsider' && trimmedName.length > 0));

  const error = (create.error ?? update.error) as Error | undefined;

  async function save() {
    if (!choice) return;
    const input = {
      seasonId,
      groupId: group.config.groupId,
      date: session.date,
      outPlayerId,
      ...(choice.kind === 'reserve'
        ? { subKind: 'reserve' as const, subPlayerId: choice.playerId }
        : { subKind: 'outsider' as const, subName: trimmedName }),
    };
    try {
      if (existing) {
        await update.mutateAsync({
          key: group.key,
          input: { ...input, expectedUpdatedAt: existing.updatedAt },
        });
      } else {
        await create.mutateAsync({ key: group.key, input });
      }
      onSaved?.();
      onClose();
    } catch {
      // Rendered below from the mutation's error; the dialog stays open so nothing is lost.
    }
  }

  return (
    <div className="sheet-backdrop" role="dialog" aria-modal="true">
      <div className="sheet">
        <h2>{handingOff ? "Who's taking your place?" : "Who's subbing for you?"}</h2>
        {handingOff && (
          <p className="muted">
            You're in for {nameOf(group, outPlayerId)}. Name who takes your place and you'll come
            off the lineup.
          </p>
        )}
        {/* The app contacts nobody, and the picker is the first thing seen after "I'm not
            available" now that the confirm step is gone — so this is the one place to say it,
            and it has to land before a name is picked. Same closing note as the reminder
            (§4.1), minus the URL you'd be reading from inside the app. */}
        <p className="muted">
          No one is notified — ask them yourself, then record who's covering here.
        </p>
        <ul className="options">
          {reserves.map((reserve) => (
            <li key={reserve.playerId}>
              <label>
                <input
                  type="radio"
                  name="sub"
                  checked={choice?.kind === 'reserve' && choice.playerId === reserve.playerId}
                  onChange={() => setChoice({ kind: 'reserve', playerId: reserve.playerId })}
                />
                {/* Named the way the card names them, and in the same order: a name on its
                    own when they can play, above the ones carrying the card's "not available"
                    note. Stated back in August — an annotation that sorts, never a filter
                    (§6.1). A reserve who has answered for *this* week is gone from the list
                    entirely, so this can only be the August forecast. */}
                <span className="option-name">
                  {reserve.name}
                  {reserve.unavailable && <span className="option-note"> — not available</span>}
                </span>
              </label>
            </li>
          ))}
          <li>
            <label>
              <input
                type="radio"
                name="sub"
                checked={choice?.kind === 'outsider'}
                onChange={() => setChoice({ kind: 'outsider' })}
              />
              <span className="option-name">Someone else</span>
            </label>
            {choice?.kind === 'outsider' && (
              <input
                className="text-input"
                type="text"
                autoFocus
                placeholder="Their name"
                maxLength={OUTSIDER_NAME_MAX}
                value={outsiderName}
                onChange={(e) => setOutsiderName(e.target.value)}
              />
            )}
          </li>
        </ul>

        {!online && <p className="error">You're offline — this can't be saved right now.</p>}
        {error && <p className="error">{messageFor(error)}</p>}

        <div className="actions">
          <button type="button" className="primary" disabled={!canSave} onClick={save}>
            {pending ? 'Saving…' : editing && !handingOff ? 'Change sub' : 'Save'}
          </button>
          <button type="button" onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function nameOf(group: GroupView, playerId: string): string {
  return group.config.players.find((p) => p.id === playerId)?.name ?? 'them';
}

function messageFor(error: Error): string {
  if (error instanceof ApiError && error.code === 'season-rolled-over') {
    return 'The schedule has been updated. Reload the app and try again.';
  }
  if (error instanceof ApiError) return error.message;
  return "Couldn't save that — check your connection and try again.";
}
